// components/tablon/TablonAnunciosForm.jsx
import React, { useState, useEffect } from "react";
import {
    Box, Typography, TextField, Button, MenuItem,
    FormControl, InputLabel, Select, Divider, Alert,
    CircularProgress, Autocomplete
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { UrlApi } from "../../services/apirest";

const PRIORIDADES = [
    { value: "normal", label: "Normal" },
    { value: "importante", label: "Importante" },
    { value: "urgente", label: "Urgente" },
]

const FORM_INICIAL = {
    titulo: "",
    mensaje: "",
    tipo: "individual",
    username_dest: null,
    prioridad: "normal",
    fecha_exp: "",
}

export default function TablonAnunciosForm({ onPublicado }) {
    const [form, setForm] = useState(FORM_INICIAL)
    const [usuarios, setUsuarios] = useState([])
    const [cargando, setCargando] = useState(false)
    const [resultado, setResultado] = useState(null) // { ok, mensaje }

    const creado_por = localStorage.getItem("user")

    // Cargamos la lista de usuarios para el selector individual
    useEffect(() => {
        const fetchUsuarios = async () => {
            try {
                const res = await fetch(`${UrlApi}/userdata`)
                const data = await res.json()
                setUsuarios(data)
            } catch (err) {
                console.error("Error al cargar usuarios:", err)
            }
        }
        fetchUsuarios()
    }, [])

    const handleChange = (field) => (e) => {
        setForm(prev => ({
            ...prev,
            [field]: e.target.value,
            // Si cambia a general, limpiamos el destino
            ...(field === "tipo" && e.target.value === "general"
                ? { username_dest: null }
                : {})
        }))
    }

    const handleSubmit = async () => {
        // Validaciones básicas
        if (!form.titulo.trim() || !form.mensaje.trim()) {
            setResultado({ ok: false, mensaje: "El título y el mensaje son obligatorios" })
            return
        }
        if (form.tipo === "individual" && !form.username_dest) {
            setResultado({ ok: false, mensaje: "Seleccioná un usuario destinatario" })
            return
        }

        setCargando(true)
        setResultado(null)

        try {
            const res = await fetch(`${UrlApi}/tablon-anuncios`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...form,
                    fecha_exp: form.fecha_exp || null,
                    creado_por,
                })
            })
            const data = await res.json()

            if (data.ok) {
                setResultado({ ok: true, mensaje: "Anuncio publicado correctamente" })
                setForm(FORM_INICIAL)
                setTimeout(() => onPublicado?.(), 1500)
            } else {
                setResultado({ ok: false, mensaje: data.error || "Error al publicar" })
            }
        } catch (err) {
            setResultado({ ok: false, mensaje: "Error de conexión con el servidor" })
        } finally {
            setCargando(false)
        }
    }

    return (
        <Box sx={{ p: 2, maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
                Publicar anuncio
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* Resultado */}
            {resultado && (
                <Alert
                    severity={resultado.ok ? "success" : "error"}
                    sx={{ mb: 2 }}
                    onClose={() => setResultado(null)}
                >
                    {resultado.mensaje}
                </Alert>
            )}

            {/* Título */}
            <TextField
                label="Título"
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                value={form.titulo}
                onChange={handleChange("titulo")}
                inputProps={{ maxLength: 150 }}
            />

            {/* Mensaje */}
            <TextField
                label="Mensaje"
                fullWidth
                size="small"
                multiline
                rows={4}
                sx={{ mb: 2 }}
                value={form.mensaje}
                onChange={handleChange("mensaje")}
            />

            {/* Tipo + Prioridad */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <FormControl size="small" fullWidth>
                    <InputLabel>Destino</InputLabel>
                    <Select
                        value={form.tipo}
                        label="Destino"
                        onChange={handleChange("tipo")}
                    >
                        <MenuItem value="general">General — todos los usuarios</MenuItem>
                        <MenuItem value="individual">Individual — un usuario</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                    <InputLabel>Prioridad</InputLabel>
                    <Select
                        value={form.prioridad}
                        label="Prioridad"
                        onChange={handleChange("prioridad")}
                    >
                        {PRIORIDADES.map(p => (
                            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Selector de usuario — solo si es individual */}
            {form.tipo === "individual" && (
                <Autocomplete
                    size="small"
                    sx={{ mb: 2 }}
                    options={usuarios}
                    getOptionLabel={(u) => `${u.username} — ${u.nombrepila} ${u.apellido}`}
                    onChange={(_, val) =>
                        setForm(prev => ({ ...prev, username_dest: val?.username || null }))
                    }
                    renderInput={(params) => (
                        <TextField {...params} label="Usuario destinatario" />
                    )}
                />
            )}

            {/* Fecha de expiración */}
            <TextField
                label="Expira el (opcional)"
                type="datetime-local"
                size="small"
                fullWidth
                sx={{ mb: 2 }}
                value={form.fecha_exp}
                onChange={handleChange("fecha_exp")}
                InputLabelProps={{ shrink: true }}
            />

            {/* Botón */}
            <Button
                variant="contained"
                endIcon={cargando ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                onClick={handleSubmit}
                disabled={cargando}
                fullWidth
            >
                {cargando ? "Publicando..." : "Publicar anuncio"}
            </Button>
        </Box>
    )
}